import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock, CheckCircle, XCircle, Package, Truck, ChevronRight, Search, X, RotateCcw } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  awaiting_acceptance: { label: 'Awaiting Acceptance', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
  preparing: { label: 'Preparing', color: 'bg-purple-100 text-purple-700', icon: Package },
  ready: { label: 'Ready', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-amber-100 text-amber-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  refunded: { label: 'Refunded', color: 'is-refunded', icon: RotateCcw },
  refund_processing: { label: 'Refund processing', color: 'bg-blue-100 text-blue-700', icon: Clock },
  checkout_cancelled: { label: 'Checkout not completed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const STATUS_OPTIONS = ['pending', 'awaiting_acceptance', 'accepted', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'completed', 'cancelled', 'rejected', 'expired'];

const isRefundedOrder = order => order.paymentStatus === 'refunded' || order.refundStatus === 'succeeded';
const isRefundProcessing = order => ['processing', 'pending'].includes(order.refundStatus) || order.paymentStatus === 'refund_pending';
const isIncompleteCardCheckout = order => {
  const unpaidPaymentState = order.paid === false || ['pending', 'failed', 'expired', 'cancelled', 'verification_failed'].includes(order.paymentStatus);
  return order.paymentMethod === 'card' && order.status === 'cancelled' && unpaidPaymentState;
};
const displayStatusForOrder = order => {
  if (isRefundedOrder(order)) return STATUS_CONFIG.refunded;
  if (isRefundProcessing(order)) return STATUS_CONFIG.refund_processing;
  if (isIncompleteCardCheckout(order)) return STATUS_CONFIG.checkout_cancelled;
  return STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
};

const PickupIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13.099 10.6562C12.8875 11.5812 12.7048 12.4267 12.4933 13.2665C12.4148 13.5816 12.519 13.8099 12.6897 14.0657C14.1622 16.2713 15.6212 18.4855 17.0846 20.6954C17.3006 21.0206 17.5483 21.3444 17.5287 21.7476C17.5015 22.3344 17.1813 22.7622 16.5893 22.9256C15.964 23.0975 15.4505 22.8793 15.0986 22.3547C14.2468 21.0799 13.4025 19.8008 12.5568 18.5231C11.4573 16.8595 10.3684 15.1888 9.25226 13.5339C9.03025 13.2043 8.9789 12.9225 9.07556 12.5323C9.35949 11.3962 9.57697 10.2458 9.85487 8.93919C8.85052 9.67775 7.88998 10.2082 7.84165 11.4555C7.81446 12.1492 7.68609 12.8416 7.58792 13.531C7.48673 14.2392 7.04723 14.628 6.47785 14.5398C5.8979 14.4502 5.59584 13.9672 5.68797 13.2737C5.82843 12.2244 5.97342 11.1765 6.10783 10.1272C6.17126 9.62716 6.39026 9.21235 6.79501 8.88716C7.62265 8.22231 8.43821 7.54156 9.28096 6.8955C10.0421 6.3116 10.9106 5.95605 11.9028 5.93726C12.7229 5.9228 13.3542 6.21765 13.7529 6.94031C14.1652 7.68754 14.5066 8.46223 14.7044 9.28028C14.9279 10.2053 15.4641 10.8065 16.3899 11.1462C17.0423 11.3861 17.6646 11.7055 18.2959 11.9989C18.9272 12.2923 19.1688 12.8011 18.8774 13.2708C18.5421 13.8085 18.0588 13.8258 17.5105 13.5671C16.8143 13.2376 16.1105 12.9225 15.4082 12.6045C13.9372 11.9397 13.9372 11.9397 13.099 10.6562Z" />
    <path d="M10.2077 16.9911C10.5762 17.7079 10.1352 18.4725 9.74406 19.0636C8.95417 20.256 8.02232 21.366 7.12521 22.4905C6.67212 23.0585 5.99702 23.1496 5.47295 22.7738C4.94586 22.3951 4.83409 21.7404 5.26604 21.1796C6.7129 19.3007 8.41047 17.5547 8.51921 14.9936" />
    <path d="M13.1715 1.00004C14.4311 0.992815 15.4158 1.93805 15.4007 3.13911C15.3871 4.25924 14.4175 5.18858 13.244 5.21026C12.001 5.23338 10.9649 4.2708 10.971 3.10009C10.9755 1.94239 11.9557 1.00727 13.1715 1.00004Z" />
  </svg>
);

const DeliveryIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M19.8438 15.0794L18.3009 10.0302L16.1882 10.6882L17.662 15.5113C16.1796 16.2322 15.1565 17.7633 15.1565 19.5357C15.1565 22.0012 17.1363 24 19.5783 24C22.0203 24 24 22.0012 24 19.5357C24 17.1602 22.1622 15.218 19.8438 15.0794ZM19.5783 21.7678C20.7993 21.7678 21.7891 20.7685 21.7891 19.5357C21.7891 18.3029 20.7993 17.3036 19.5783 17.3036C18.3572 17.3036 17.3674 18.3029 17.3674 19.5357C17.3674 20.7685 18.3572 21.7678 19.5783 21.7678Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M6.31306 23.9998C8.75511 23.9998 10.7348 22.001 10.7348 19.5355C10.7348 17.07 8.75511 15.0713 6.31306 15.0713C3.87101 15.0713 1.89133 17.07 1.89133 19.5355C1.89133 22.001 3.87101 23.9998 6.31306 23.9998ZM6.31306 21.7676C7.53409 21.7676 8.52392 20.7683 8.52392 19.5355C8.52392 18.3027 7.53409 17.3034 6.31306 17.3034C5.09203 17.3034 4.1022 18.3027 4.1022 19.5355C4.1022 20.7683 5.09203 21.7676 6.31306 21.7676Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M15.1565 6.14274C16.3776 6.14274 17.3674 5.14338 17.3674 3.91061C17.3674 2.67784 16.3776 1.67848 15.1565 1.67848C13.9355 1.67848 12.9457 2.67784 12.9457 3.91061C12.9457 5.14338 13.9355 6.14274 15.1565 6.14274Z" />
    <path fillRule="evenodd" clipRule="evenodd" d="M11.3107 7.53533C12.134 6.49648 13.6687 6.41066 14.6005 7.35137L16.7197 9.49095H19.5781C20.1887 9.49095 20.6836 9.99064 20.6836 10.607C20.6836 11.2234 20.1887 11.7231 19.5781 11.7231H16.5162C16.0601 11.7231 15.6228 11.5402 15.3002 11.2146L13.0372 8.92973L10.1402 12.5857L13.7513 15.0163C14.356 15.4233 14.642 16.1701 14.4658 16.8819L13.4652 20.9223C13.3172 21.5203 12.717 21.8839 12.1247 21.7343C11.5324 21.5849 11.1723 20.9789 11.3204 20.3809L12.2369 16.6797L8.91384 14.443C7.835 13.7169 7.60383 12.2136 8.41381 11.1914L11.3107 7.53533Z" />
    <rect width="7.21058" height="8.74543" rx="2" transform="matrix(0.763004 0.646394 -0.639173 0.769063 5.58984 0)" />
  </svg>
);

const orderDate = value => new Date(value && !/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? `${value}Z` : value);
const relativeOrderTime = value => {
  const date = orderDate(value);
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 172800) return 'yesterday';
  return `${Math.floor(seconds / 86400)} days ago`;
};

const isoLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const placedDateParams = (mode, selectedMonth, rangeStart, rangeEnd) => {
  const today = new Date();
  const todayValue = isoLocalDate(today);
  if (mode === 'today') return { fromDate: todayValue, toDate: todayValue };
  if (mode === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const value = isoLocalDate(yesterday);
    return { fromDate: value, toDate: value };
  }
  if (mode === 'week') {
    const monday = new Date(today);
    const day = monday.getDay() || 7;
    monday.setDate(monday.getDate() - day + 1);
    return { fromDate: isoLocalDate(monday), toDate: todayValue };
  }
  if (mode === 'month') {
    return { fromDate: `${todayValue.slice(0, 7)}-01`, toDate: todayValue };
  }
  if (mode === 'specific_month' && selectedMonth) {
    const [year, month] = selectedMonth.split('-').map(Number);
    return {
      fromDate: `${selectedMonth}-01`,
      toDate: isoLocalDate(new Date(year, month, 0))
    };
  }
  if (mode === 'range') return {
    ...(rangeStart ? { fromDate: rangeStart } : {}),
    ...(rangeEnd ? { toDate: rangeEnd } : {})
  };
  return {};
};

export default function AdminOrders({ onCountChange }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [refundingOrder, setRefundingOrder] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => isoLocalDate(new Date()).slice(0, 7));
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 15;

  const load = async (requestedPage = page) => {
    try {
      const params = {
        page: requestedPage,
        pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        type: typeFilter === 'all' ? undefined : typeFilter,
        ...placedDateParams(dateFilter, selectedMonth, rangeStart, rangeEnd)
      };
      const [data, summary] = await Promise.all([
        api.getAdminOrders(params),
        api.getAdminOrderSummary()
      ]);
      setOrders(data.items || []);
      setTotalOrders(data.total || 0);
      setTotalPages(data.totalPages || 1);
      if (onCountChange) onCountChange(summary.pendingCount || 0);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => load(page), searchQuery ? 450 : 0);
    return () => window.clearTimeout(timer);
  }, [page, searchQuery, statusFilter, typeFilter, dateFilter, selectedMonth, rangeStart, rangeEnd]);

  const handleStatusChange = async (orderId, newStatus) => {
  setUpdatingStatus(orderId);

  // ✅ optimistic update
  setOrders(prev =>
    prev.map(o =>
      o.id === orderId ? { ...o, status: newStatus } : o
    )
  );

  try {
    await api.updateOrderStatus(orderId, newStatus);

    // optional: reload after delay
    setTimeout(() => load(page), 500);

  } catch (err) {
    alert('Failed to update status');
    load(page); // rollback
  } finally {
    setUpdatingStatus(null);
    setStatusDialog(null);
  }
};

  const requestStatusChange = (order, newStatus) => {
    if (newStatus === 'cancelled' || newStatus === 'rejected') {
      setStatusDialog({ order, status: newStatus });
      return;
    }
    handleStatusChange(order.id, newStatus);
  };

  const availableStatusActions = order => {
    const activeStatuses = ['awaiting_acceptance', 'accepted', 'preparing', 'ready', 'out_for_delivery', 'delivered'];
    if (
      !activeStatuses.includes(order.status)
      || isRefundedOrder(order)
      || isRefundProcessing(order)
      || isIncompleteCardCheckout(order)
    ) return [];

    const progress = order.type === 'delivery'
      ? ['accepted', 'preparing', 'out_for_delivery', 'delivered', 'completed']
      : ['accepted', 'preparing', 'ready', 'completed'];
    const currentIndex = progress.indexOf(order.status);
    const nextStatuses = currentIndex >= 0 ? progress.slice(currentIndex + 1) : progress;
    return [...nextStatuses, 'cancelled', 'rejected'];
  };

  const openRefundDialog = order => {
    setRefundDialog({ stage: 'confirm', order, message: '' });
  };

  const handleRefund = async (order) => {
    const amount = Number(order.total || 0).toFixed(2);
    setRefundingOrder(order.id);
    try {
      const result = await api.refundOrder(order.id);
      if (result?.order) {
        setOrders(previous => previous.map(item => item.id === order.id ? result.order : item));
      } else {
        await load(page);
      }
      setRefundDialog({
        stage: 'success',
        order: result?.order || order,
        message: `£${amount} returned to customer.`
      });
    } catch (err) {
      setRefundDialog({
        stage: 'error',
        order,
        message: err.response?.data?.detail || 'The refund could not be completed. No second refund request was sent.'
      });
      await load(page);
    } finally {
      setRefundingOrder(null);
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const visibleOrders = orders;
  const changePage = (nextPage) => {
    setPage(nextPage);
    setExpandedOrder(null);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  useEffect(() => { setPage(1); setExpandedOrder(null); }, [searchQuery, statusFilter, typeFilter, dateFilter, selectedMonth, rangeStart, rangeEnd]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Orders</h2>
          <p className="text-gray-500 text-sm">{totalOrders} matching orders</p>
        </div>
        <div className="admin-toolbar admin-orders-toolbar">
          <div className="admin-search-field">
            <Search className="w-4 h-4" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search order or customer..." />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(status => <option key={status} value={status}>{STATUS_CONFIG[status].label}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} aria-label="Filter by order type">
            <option value="all">All types</option>
            <option value="delivery">Delivery</option>
            <option value="pickup">Pickup</option>
            <option value="collection">Collection</option>
          </select>
          <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} aria-label="Filter by placed date">
            <option value="all">All dates</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="specific_month">Specific month</option>
            <option value="range">Between dates</option>
          </select>
          {dateFilter === 'specific_month' && (
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} aria-label="Placed month" />
          )}
          {dateFilter === 'range' && (
            <>
              <input type="date" value={rangeStart} max={rangeEnd || undefined} onChange={e => setRangeStart(e.target.value)} aria-label="Placed date from" />
              <span className="admin-date-filter-separator">to</span>
              <input type="date" value={rangeEnd} min={rangeStart || undefined} onChange={e => setRangeEnd(e.target.value)} aria-label="Placed date to" />
            </>
          )}
          <span className="admin-result-count">{totalOrders} results</span>
        </div>
        <button onClick={() => load(page)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div></AdminTopbarPortal>

      <div className="admin-orders-table-shell">
        <table className="admin-orders-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Order type</th>
              <th>Payment method</th>
              <th>Placed</th>
              <th>Items</th>
              <th className="admin-orders-table-total-heading">Total</th>
              <th aria-label="Order details" />
            </tr>
          </thead>
          <tbody>
        {visibleOrders.map(order => {
          const statusInfo = displayStatusForOrder(order);
          const orderIsRefunded = isRefundedOrder(order);
          const orderIsIncompleteCheckout = isIncompleteCardCheckout(order);
          const paymentStatusLabel = orderIsRefunded
            ? 'Refunded'
            : orderIsIncompleteCheckout
              ? 'Not paid'
              : (order.paymentStatus || 'unknown').replaceAll('_', ' ');
          const isExpanded = expandedOrder === order.id;
          const customerName = order.customerName || order.guestName || order.user?.name || 'Guest customer';

          return (
            <React.Fragment key={order.id}>
              <tr className={`admin-order-table-row ${isExpanded ? 'is-expanded' : ''}`} onClick={() => toggleExpand(order.id)}>
                <td data-label="Order">
                  <div className="admin-order-identity">
                  <div className="admin-order-title-row">
                    <p className="font-semibold text-gray-900 text-sm">Order #{order.orderNumber}</p>
                  </div>
                  </div>
                </td>
                <td data-label="Status" className="admin-order-status-cell">
                  <span className={`admin-order-status-pill capitalize ${statusInfo.color}`}>{statusInfo.label}</span>
                </td>
                <td data-label="Customer">
                  <strong>{customerName}</strong>
                </td>
                <td data-label="Order type" className="admin-order-fulfilment">
                  <strong className="admin-order-type capitalize">
                    {order.type === 'delivery' ? <DeliveryIcon /> : <PickupIcon />}
                    {order.type === 'collection' ? 'Pickup' : order.type}
                  </strong>
                </td>
                <td data-label="Payment method" className="admin-order-payment">
                  <strong className={`admin-payment-pill ${order.paymentMethod === 'cash' ? 'is-cash' : 'is-card'}`}>
                    {order.paymentMethod === 'cash' ? 'Cash' : 'Card'}
                  </strong>
                </td>
                <td data-label="Placed" className="admin-order-time">
                  <strong>{relativeOrderTime(order.createdAt)}</strong>
                  <p className="admin-order-meta">
{orderDate(order.createdAt).toLocaleString("en-GB", {
  timeZone: "Europe/London",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
})}
                  </p>
                </td>
                <td data-label="Items" className="admin-order-items-cell">
                  <strong>{order.items?.length || 0} {(order.items?.length || 0) === 1 ? 'item' : 'items'}</strong>
                </td>
                <td data-label="Total" className="admin-order-table-total">
                  <span className="font-bold text-gray-900">£{order.total?.toFixed(2)}</span>
                </td>
                <td className="admin-order-table-toggle">
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </td>
              </tr>

              {/* Expanded Details */}
              {isExpanded && (
                <tr className="admin-order-detail-row" onMouseDown={() => setExpandedOrder(null)}>
                <td colSpan="9" className="admin-order-drawer" onMouseDown={event => event.stopPropagation()}>
                <div className="admin-order-drawer-header">
                  <div><span>Order details</span><h3>Order #{order.orderNumber}</h3><p>Reference {order.id.slice(0, 8)}</p></div>
                  <button onClick={() => setExpandedOrder(null)} aria-label="Close order details"><X /></button>
                </div>
                <div className="admin-order-detail-panel">
                  <div className="admin-order-drawer-summary">
                    <span className={`admin-order-status-pill capitalize ${statusInfo.color}`}>{statusInfo.label}</span>
                    <strong className="admin-order-type capitalize">
                      {order.type === 'delivery' ? <DeliveryIcon /> : <PickupIcon />}
                      {order.type === 'collection' ? 'Pickup' : order.type}
                    </strong>
                    <strong className={`admin-payment-pill ${order.paymentMethod === 'cash' ? 'is-cash' : 'is-card'}`}>
                      {order.paymentMethod === 'cash' ? 'Cash' : 'Card'}
                    </strong>
                    <strong className="admin-order-drawer-total">£{order.total?.toFixed(2)}</strong>
                  </div>

                  <div className="admin-order-contact-grid">
                    {/* Customer Info */}
                   <div className="admin-order-info-block">
                    <h4>Customer</h4>

                    <p className="admin-order-info-primary">
                      {order.customerName || order.guestName || order.user?.name || 'Registered User'}
                    </p>

                    {(order.email || order.guestEmail || order.user?.email) && (
                      <p>
                        {order.email || order.guestEmail || order.user?.email}
                      </p>
                    )}

                    {(order.guestPhone || order.user?.phone) && (
                      <p>
                        {order.guestPhone || order.user?.phone}
                      </p>
                    )}
                  </div>

                    {/* Delivery Address */}
                    {order.type === 'delivery' && order.deliveryAddress && (
                      <div className="admin-order-info-block">
                        <h4>Delivery address</h4>
                        <p className="admin-order-info-primary">{order.deliveryAddress.line1}</p>
                        {order.deliveryAddress.line2 && <p>{order.deliveryAddress.line2}</p>}
                        <p>{order.deliveryAddress.city}, {order.deliveryAddress.postcode}</p>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <section className="admin-order-drawer-section">
                      <h4>Notes</h4>
                      <p className="admin-order-drawer-note">{order.notes}</p>
                    </section>
                  )}

                  {/* Items */}
                  <section className="admin-order-drawer-section">
                    <h4>Items</h4>
                    <div className="admin-order-drawer-items">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="admin-order-drawer-item">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="admin-order-item-quantity">{item.quantity}</span>
                              <div className="flex-1">
                                <span className="text-sm text-gray-800 font-medium">{item.name}</span>
                                {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {Object.entries(item.selectedOptions).map(([optionId, choice]) => {
                                      if (Array.isArray(choice)) {
                                        // Multiple choice (e.g., toppings)
                                        const names = choice.map(c => c.name).filter(Boolean).join(', ');
                                        return names ? <div key={optionId}>+ {names}</div> : null;
                                      } else if (choice && choice.name) {
                                        // Single choice (e.g., size)
                                        return <div key={optionId}>{choice.name}</div>;
                                      }
                                      return null;
                                    }).filter(Boolean)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">£{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Order Summary */}
                  <section className="admin-order-drawer-section">
                    <h4>Payment summary</h4>
                    <div className="admin-order-drawer-totals space-y-1.5 text-sm">
                      <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>£{order.subtotal?.toFixed(2)}</span></div>
                      {order.deliveryFee > 0 && <div className="flex justify-between text-gray-600"><span>Delivery Fee</span><span>£{order.deliveryFee?.toFixed(2)}</span></div>}
                      {order.serviceCharge > 0 && <div className="flex justify-between text-gray-600"><span>Service Charge</span><span>£{order.serviceCharge?.toFixed(2)}</span></div>}
                      {order.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>−£{order.discount?.toFixed(2)}</span></div>}
                      <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-200"><span>Total</span><span>£{order.total?.toFixed(2)}</span></div>
                      <div className="admin-payment-detail-row"><span>Payment status</span><strong>{paymentStatusLabel}</strong></div>
                      {order.stripePaymentIntentId && (
                        <div className="admin-payment-detail-row"><span>Payment reference</span><code>{order.stripePaymentIntentId}</code></div>
                      )}
                      {order.refund?.id && (
                        <div className="admin-payment-detail-row"><span>Refund reference</span><code>{order.refund.id}</code></div>
                      )}
                    </div>
                  </section>

                  {order.paymentMethod === 'card' && (
                    <section className="admin-order-drawer-section admin-order-refund-section">
                      <h4>Refund</h4>
                      {orderIsRefunded ? (
                        <div className="admin-refund-state is-success">
                          <CheckCircle />
                          <div><strong>Fully refunded</strong><span>£{Number(order.refund?.amount || order.total || 0).toFixed(2)} returned through Stripe.</span></div>
                        </div>
                      ) : ['processing', 'pending'].includes(order.refundStatus) || order.paymentStatus === 'refund_pending' ? (
                        <div className="admin-refund-state">
                          <Clock />
                          <div><strong>Refund processing</strong><span>Stripe will update this order when the refund is complete.</span></div>
                        </div>
                      ) : orderIsIncompleteCheckout ? (
                        <p className="admin-refund-unavailable">No card payment was taken for this checkout.</p>
                      ) : order.paymentStatus === 'paid' && (order.stripePaymentIntentId || order.paymentId) ? (
                        <div className="admin-refund-action">
                          <p>Returns the full £{Number(order.total || 0).toFixed(2)} card payment. Partial refunds are not enabled.</p>
                          <button type="button" onClick={() => openRefundDialog(order)} disabled={refundingOrder === order.id}>
                            <RotateCcw />
                            {refundingOrder === order.id ? 'Refunding…' : `Refund £${Number(order.total || 0).toFixed(2)}`}
                          </button>
                        </div>
                      ) : order.paymentStatus === 'paid' ? (
                        <div className="admin-refund-state is-warning">
                          <XCircle />
                          <div><strong>Payment reference unavailable</strong><span>This older order cannot be refunded until its payment reference is reconciled.</span></div>
                        </div>
                      ) : (
                        <p className="admin-refund-unavailable">A refund becomes available after Stripe confirms this card payment.</p>
                      )}
                    </section>
                  )}

                  {/* Status Update */}
                  {availableStatusActions(order).length > 0 && (
                    <section className="admin-order-drawer-section admin-order-drawer-status">
                      <h4>Update status</h4>
                      <div className="admin-order-status-actions">
                        {availableStatusActions(order).map(status => {
                          const config = STATUS_CONFIG[status];
                          return (
                            <button key={status}
                              onClick={() => requestStatusChange(order, status)}
                              disabled={updatingStatus === order.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-60">
                              {config.label}
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </div>
                </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
          </tbody>
        </table>
      </div>
      {orders.length === 0 && <div className="admin-empty-state"><Package className="w-6 h-6" />No orders match those filters.</div>}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 1} onClick={() => changePage(page - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => changePage(page + 1)}>Next</button>
        </div>
      )}

      {refundDialog && (
        <div className="admin-confirmation-backdrop" onMouseDown={() => !refundingOrder && setRefundDialog(null)}>
          <div className="admin-confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="refund-dialog-title" onMouseDown={event => event.stopPropagation()}>
            <button className="admin-confirmation-close" type="button" onClick={() => setRefundDialog(null)} disabled={Boolean(refundingOrder)} aria-label="Close refund confirmation">
              <X />
            </button>

            {refundDialog.stage === 'confirm' && (
              <>
                <span className="admin-confirmation-icon is-warning"><RotateCcw /></span>
                <h3 id="refund-dialog-title">Refund Order #{refundDialog.order.orderNumber}?</h3>
                <p>The full <strong>£{Number(refundDialog.order.total || 0).toFixed(2)}</strong> card payment will be returned to the customer through Stripe.</p>
                <p className="admin-confirmation-note">This cannot be undone in Orderkit.</p>
                <div className="admin-confirmation-actions">
                  <button type="button" className="is-secondary" onClick={() => setRefundDialog(null)}>Cancel</button>
                  <button type="button" className="is-danger" onClick={() => handleRefund(refundDialog.order)} disabled={Boolean(refundingOrder)}>
                    {refundingOrder ? 'Refunding…' : `Refund £${Number(refundDialog.order.total || 0).toFixed(2)}`}
                  </button>
                </div>
              </>
            )}

            {refundDialog.stage === 'success' && (
              <>
                <span className="admin-confirmation-icon is-success"><CheckCircle /></span>
                <h3 id="refund-dialog-title">Refund complete</h3>
                <p className="admin-confirmation-result">{refundDialog.message}</p>
                <div className="admin-confirmation-actions is-single">
                  <button type="button" className="is-primary" onClick={() => setRefundDialog(null)}>Done</button>
                </div>
              </>
            )}

            {refundDialog.stage === 'error' && (
              <>
                <span className="admin-confirmation-icon is-error"><XCircle /></span>
                <h3 id="refund-dialog-title">Refund not completed</h3>
                <p>{refundDialog.message}</p>
                <div className="admin-confirmation-actions is-single">
                  <button type="button" className="is-primary" onClick={() => setRefundDialog(null)}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {statusDialog && (
        <div className="admin-confirmation-backdrop" onMouseDown={() => !updatingStatus && setStatusDialog(null)}>
          <div className="admin-confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="status-dialog-title" onMouseDown={event => event.stopPropagation()}>
            <button className="admin-confirmation-close" type="button" onClick={() => setStatusDialog(null)} disabled={Boolean(updatingStatus)} aria-label="Close status confirmation">
              <X />
            </button>
            <span className="admin-confirmation-icon is-warning"><XCircle /></span>
            <h3 id="status-dialog-title">{statusDialog.status === 'cancelled' ? 'Cancel' : 'Reject'} Order #{statusDialog.order.orderNumber}?</h3>
            <p>The order will be marked as <strong>{STATUS_CONFIG[statusDialog.status].label}</strong> for the restaurant and customer.</p>
            <p className="admin-confirmation-note">Confirm this is the correct order before continuing.</p>
            <div className="admin-confirmation-actions">
              <button type="button" className="is-secondary" onClick={() => setStatusDialog(null)} disabled={Boolean(updatingStatus)}>Go back</button>
              <button type="button" className="is-danger" onClick={() => handleStatusChange(statusDialog.order.id, statusDialog.status)} disabled={Boolean(updatingStatus)}>
                {updatingStatus ? 'Updating…' : statusDialog.status === 'cancelled' ? 'Cancel order' : 'Reject order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
